import {ContentTileWithTitleAndToggleSeparated} from '#/atoms/content-tile';
import {VerticalSeparator} from '#/atoms/vertical-separator';
import {ActionMenuContext} from '#/components/actionmenu/action-menu';
import {
  ActionMenuContentSectionInfoRow,
  ActionMenuContentSectionInfoRowRentalYield,
} from '#/components/actionmenu/subcomponents';
import {TextColorMarketIndicator} from '#/components/indicators/market-indicator';
import {WalletContext} from '#/contexts/wallet-provider';
import {useLandTransactionsAmountMarketData} from '#/hooks/query/use-land-transactions-amount-market-data';
import {useCoinDisplayPreferences} from '#/hooks/use-coin-display-preferences';
import {decimalsMultiplier} from '#/utils/contract-consts';
import {getNumberPositivityStatus} from '#/utils/number-utils';
import {DefaultChildren} from '#/utils/utils';
import {ArrowUpOnSquareIcon} from '@heroicons/react/24/solid';
import {Land, LandTransactionsMarketData, TransactionType, getLandPremium} from '@shareland/shareland-common';
import {KeyPrefix, ParseKeysByKeyPrefix} from 'i18next';
import {FunctionComponent, useContext} from 'react';
import {Trans, useTranslation} from 'react-i18next';

export const ActionMenuContentSectionInformation: FunctionComponent<DefaultChildren> = () => {
  const {t: tBase} = useTranslation('translation');
  const {t} = useTranslation('translation', {
    keyPrefix: 'pages.actionMenu.information.sections.information.contentSection.information',
  });

  // Context
  const {land, landMarketInfo} = useContext(ActionMenuContext);
  const {
    accountResult: {chain},
  } = useContext(WalletContext);

  // External hooks
  const displayPref = useCoinDisplayPreferences();
  const {data: landTransactionsAmountMarketData} = useLandTransactionsAmountMarketData(land);

  return (
    <ContentTileWithTitleAndToggleSeparated title={t('title')} contentClassName='text-white space-y-2'>
      {{
        opened: (
          <>
            <ActionMenuContentSectionInfoRow>
              {{
                left: t('infoRows.marketPrice.title'),
                right: t('infoRows.marketPrice.value', {
                  currencySymbol: displayPref.symbol,
                  amount: displayPref.displayAmount(landMarketInfo.marketPrice),
                }),
                tooltip: t('infoRows.marketPrice.tooltip'),
              }}
            </ActionMenuContentSectionInfoRow>

            <ActionMenuContentSectionInfoRow>
              {{
                left: t('infoRows.oraclePrice.title'),
                right: t('infoRows.oraclePrice.value', {
                  currencySymbol: displayPref.symbol,
                  amount: displayPref.displayAmount(landMarketInfo.oraclePrice),
                }),
                tooltip: t('infoRows.oraclePrice.tooltip'),
              }}
            </ActionMenuContentSectionInfoRow>

            <ActionMenuContentSectionInfoRow>
              {{
                left: t('infoRows.premium.title'),
                right: t('infoRows.premium.value', {
                  value: getLandPremium(landMarketInfo.marketPrice, landMarketInfo.oraclePrice),
                }),
                tooltip: t('infoRows.premium.tooltip'),
              }}
            </ActionMenuContentSectionInfoRow>

            <ActionMenuContentSectionInfoRowRentalYield land={land} />

            <ActionMenuContentSectionInfoRow>
              {{
                left: t('infoRows.volume24.title'),
                right: t('infoRows.volume24.value', {
                  currencySymbol: displayPref.symbol,
                  amount: displayPref.displayAmount(land.syntheticMarketData.dailyVolume),
                }),
              }}
            </ActionMenuContentSectionInfoRow>

            <ActionMenuContentSectionInfoRow>
              {{
                left: t('infoRows.landVolume24.title'),
                right: t('infoRows.landVolume24.value', {
                  currencySymbol: landMarketInfo.symbol,
                  amount: land.syntheticMarketData.dailyLandVolume,
                }),
              }}
            </ActionMenuContentSectionInfoRow>
          </>
        ),
        toOpen: (
          <>
            <VerticalSeparator />

            <ActionMenuContentSectionInfoRow>
              {{
                left: t('infoRows.marketCap.title'),
                right: t('infoRows.marketCap.value', {
                  currencySymbol: displayPref.symbol,
                  amount: displayPref.displayAmount(
                    (landMarketInfo.totalSupply / decimalsMultiplier) * landMarketInfo.marketPrice,
                  ),
                }),
              }}
            </ActionMenuContentSectionInfoRow>

            <ActionMenuContentSectionInfoRow>
              {{
                left: t('infoRows.liquidity.title'),
                right: t('infoRows.liquidity.value', {
                  currencySymbol: displayPref.symbol,
                  amount: displayPref.displayAmount(landMarketInfo.totalLiquidity),
                }),
                tooltip: t('infoRows.liquidity.tooltip'),
              }}
            </ActionMenuContentSectionInfoRow>
            <ActionMenuContentSectionInfoRow>
              {{
                left: t('infoRows.tvl.title'),
                right: t('infoRows.tvl.value', {
                  currencySymbol: displayPref.symbol,
                  amount: displayPref.displayAmount(land.syntheticMarketData.tvl),
                }),
              }}
            </ActionMenuContentSectionInfoRow>

            <ActionMenuContentSectionInfoRow>
              {{
                left: t('infoRows.circulatingSupply.title'),
                right: t('infoRows.circulatingSupply.value', {
                  amount: landMarketInfo.totalSupply,
                }),
              }}
            </ActionMenuContentSectionInfoRow>

            <ActionMenuContentSectionInfoRow>
              {{
                left: t('infoRows.supply24.title'),
                right: (
                  <Trans
                    t={t}
                    i18nKey='infoRows.supply24.value'
                    values={{
                      amount: land.syntheticMarketData.dailySupplyDifference,
                    }}
                    components={{
                      TextColorMarketIndicator: (
                        <TextColorMarketIndicator
                          numberPositivity={getNumberPositivityStatus(land.syntheticMarketData.dailySupplyDifference)}
                        />
                      ),
                    }}
                  />
                ),
              }}
            </ActionMenuContentSectionInfoRow>

            {chain?.nativeCurrency.symbol === 'MATIC' && (
              <ActionMenuContentSectionInfoRow>
                {{
                  left: t('infoRows.holders.title'),
                  right: (
                    <Trans
                      t={t}
                      i18nKey='infoRows.holders.value'
                      components={{
                        CTAOpenHolderChart: <CTAOpenHolderChart land={land} />,
                      }}
                    />
                  ),
                }}
              </ActionMenuContentSectionInfoRow>
            )}

            <VerticalSeparator />

            <ActionMenuContentSectionInfoRow>
              {{
                left: t('infoRows.demand.title'),
                right: !landTransactionsAmountMarketData ? (
                  tBase('pages.actionMenu.components.contentSection.infoRow.empty')
                ) : (
                  <Trans
                    t={t}
                    i18nKey='infoRows.demand.value'
                    values={{
                      amount: landTransactionsAmountMarketData.demand,
                    }}
                    components={{
                      TextColorMarketIndicator: (
                        <TextColorMarketIndicator
                          numberPositivity={getNumberPositivityStatus(landTransactionsAmountMarketData.demand)}
                        />
                      ),
                    }}
                  />
                ),
                tooltip: t('infoRows.demand.tooltip'),
              }}
            </ActionMenuContentSectionInfoRow>

            <ActionMenuContentSectionInfoRow>
              {{
                left: t('infoRows.transactions24.title'),
                right: !landTransactionsAmountMarketData
                  ? tBase('pages.actionMenu.components.contentSection.infoRow.empty')
                  : t('infoRows.transactions24.value', {
                      value: landTransactionsAmountMarketData.total,
                    }),
              }}
            </ActionMenuContentSectionInfoRow>

            <Transaction24Type
              keyPrefix='transactions24Buy'
              transactionType={TransactionType.Purchase}
              landTransactionsMarketData={landTransactionsAmountMarketData}
            />
            <Transaction24Type
              keyPrefix='transactions24Sell'
              transactionType={TransactionType.Sell}
              landTransactionsMarketData={landTransactionsAmountMarketData}
            />
            <Transaction24Type
              keyPrefix='transactions24BorrowOpen'
              transactionType={TransactionType.OpenCollateralPosition}
              landTransactionsMarketData={landTransactionsAmountMarketData}
            />
            <Transaction24Type
              keyPrefix='transactions24BorrowAdjust'
              transactionType={TransactionType.AdjustCollateralPosition}
              landTransactionsMarketData={landTransactionsAmountMarketData}
            />
            <Transaction24Type
              keyPrefix='transactions24BorrowClose'
              transactionType={TransactionType.CloseCollateralPosition}
              landTransactionsMarketData={landTransactionsAmountMarketData}
            />
          </>
        ),
      }}
    </ContentTileWithTitleAndToggleSeparated>
  );
};

const CTAOpenHolderChart: FunctionComponent<{land: Land} & DefaultChildren> = ({children, land}) => (
  <a
    className='flex items-center gap-1 text-deepblue hover:opacity-70'
    href={`https://mumbai.polygonscan.com/token/tokenholderchart/${land?.contractAddresses.shareLandToken}`}
    target='_blank'
    rel='noreferrer'
  >
    {children}
    <ArrowUpOnSquareIcon className='w-3 h-3 stroke-deepblue fill-transparent' />
  </a>
);

const keyPrefixInfoRows =
  'pages.actionMenu.information.sections.information.contentSection.information.infoRows' as const;
type Transaction24TypeProps = {
  keyPrefix: ParseKeysByKeyPrefix<KeyPrefix<'translation'>, typeof keyPrefixInfoRows>;
  transactionType: TransactionType;
  landTransactionsMarketData: LandTransactionsMarketData | undefined;
};

const Transaction24Type: FunctionComponent<Transaction24TypeProps> = ({
  keyPrefix,
  transactionType,
  landTransactionsMarketData,
}) => {
  const {t: tBase} = useTranslation('translation');
  const {t} = useTranslation('translation', {
    keyPrefix: `${keyPrefixInfoRows}.${keyPrefix}`,
  });

  const total = landTransactionsMarketData?.total;
  const count = landTransactionsMarketData?.byTransactionType[transactionType];

  return (
    <ActionMenuContentSectionInfoRow>
      {{
        left: t('title'),
        right:
          total === undefined || count === undefined
            ? tBase('pages.actionMenu.components.contentSection.infoRow.empty')
            : t('value', {value: count === 0 ? 0 : count / total}),
      }}
    </ActionMenuContentSectionInfoRow>
  );
};
